import { z } from 'zod';
import { eventCodeSchema } from '../events/catalogue.js';
import { carrierResolutionSchema } from '../status/carrier.js';
import { orderStatusSchema } from '../status/order.js';
import { storeCodeSchema } from '../status/store.js';
import { unifiedOrderStatusSchema } from '../status/unified-order.js';
import { pennylaneInvoiceStatusSchema } from '../status/pennylane.js';
import { dateRangeQuerySchema, decimalStringSchema, handledByV1Schema, isoDateTimeSchema, MAX_PAGE_SIZE, objectIdSchema, paginationQuerySchema, sortQuerySchema, queryBooleanSchema, } from './common.js';
/** Avertissement porté par une commande (`03` §2, `orders.transform.warnings`). */
export const orderWarningSchema = z.object({
    code: z.string(),
    field: z.string().optional(),
    message: z.string(),
});
/** Transporteur retenu par le pipeline (`03` §2, `orders.transform.carrier`). */
export const orderCarrierSchema = z.object({
    code: z.string(),
    label: z.string(),
    ruleId: objectIdSchema.nullable(),
    /** Libellé de la règle retenue ; `null` quand le transporteur par défaut s'applique. */
    ruleName: z.string().nullable(),
    resolution: carrierResolutionSchema,
    pickup: z.boolean(),
});
/** Origine d'une ligne CSV (`03` §2, `orders.transform.lines[].origin`). */
export const orderLineOriginSchema = z.object({
    type: z.enum(['line', 'bundle', 'operation', 'system_rule']),
    sourceLineId: z.string().nullable(),
    bundleSku: z.string().nullable(),
    operationId: objectIdSchema.nullable(),
});
export const orderLineSchema = z.object({
    sku: z.string(),
    qty: z.number().int().positive(),
    unitPrice: decimalStringSchema,
    discount: decimalStringSchema,
    origin: orderLineOriginSchema,
});
/**
 * Ligne « avant » (Shopify), tableau du haut de l'onglet Lignes (`02` §4,
 * onglet 2). `unitPrice` / `discount` sont `null` quand le snapshot ne porte
 * pas le champ attendu (commande jamais préparée, payload partiel).
 */
export const orderLineBeforeSchema = z.object({
    id: z.string(),
    sku: z.string().nullable(),
    title: z.string().nullable(),
    quantity: z.number().int().min(0),
    currentQuantity: z.number().int().min(0),
    unitPrice: decimalStringSchema.nullable(),
    discount: decimalStringSchema.nullable(),
});
export const orderTrackingSchema = z.object({
    number: z.string(),
    url: z.string(),
    company: z.string(),
});
/** Ligne de la liste « Commandes » (`02` §3). */
export const orderListItemSchema = z.object({
    id: objectIdSchema,
    store: storeCodeSchema,
    orderId: z.string(),
    name: z.string(),
    customerName: z.string().nullable(),
    email: z.string().nullable(),
    paidAt: isoDateTimeSchema.nullable(),
    /** Date de création chez Shopify — la « Date » affichée dans la liste. */
    orderCreatedAt: isoDateTimeSchema.nullable(),
    sendAt: isoDateTimeSchema.nullable(),
    sentAt: isoDateTimeSchema.nullable(),
    status: orderStatusSchema,
    simulated: z.boolean().optional(),
    handledByV1: handledByV1Schema.nullable().optional(),
    statusReason: z.string().nullable(),
    carrier: z.object({ code: z.string(), label: z.string() }).nullable(),
    warnings: z.array(orderWarningSchema),
    currentFileId: objectIdSchema.nullable(),
    currentFileName: z.string().nullable(),
});
/** Filtres de l'écran « Commandes » (`02` §3), conservés dans l'URL. */
export const orderListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    store: storeCodeSchema.optional(),
    status: z.array(orderStatusSchema).optional(),
    search: z.string().min(1).optional(),
    operationId: objectIdSchema.optional(),
    withWarnings: queryBooleanSchema.optional(),
});
/**
 * Origine d'une ligne de la liste unifiée : une boutique Shopify
 * (`kind: 'shopify'`, `code: 'fr'`) ou Pennylane (`kind: 'pennylane'`).
 */
export const orderOriginSchema = z.object({
    kind: z.enum(['shopify', 'pennylane']),
    code: z.string(),
    label: z.string(),
});
/**
 * Ligne de la liste **Commandes** unifiée (`02` §3 après la refonte du
 * 10/09/2026) : commandes Shopify et factures Pennylane dans la même table,
 * un seul statut, mêmes colonnes. `order` / `invoice` portent le strict
 * minimum dont les actions de ligne ont besoin — exactement l'un des deux est
 * non nul, selon `origin.kind`.
 */
export const unifiedOrderListItemSchema = z.object({
    id: objectIdSchema,
    origin: orderOriginSchema,
    number: z.string(),
    customerName: z.string().nullable(),
    email: z.string().nullable(),
    date: isoDateTimeSchema,
    status: unifiedOrderStatusSchema,
    simulated: z.boolean().optional(),
    handledByV1: handledByV1Schema.nullable().optional(),
    /** Précision affichée sous le badge (« à 14:35 », « en file »…). */
    statusHint: z.string().nullable(),
    warnings: z.array(orderWarningSchema),
    order: z
        .object({
        status: orderStatusSchema,
        statusReason: z.string().nullable(),
        currentFileId: objectIdSchema.nullable(),
        currentFileName: z.string().nullable(),
    })
        .nullable(),
    invoice: z
        .object({
        status: pennylaneInvoiceStatusSchema,
        invoiceId: z.string(),
        reason: z.string().nullable(),
        fileId: objectIdSchema.nullable(),
    })
        .nullable(),
});
/** Filtres de la liste unifiée. `origin`/`status` acceptent plusieurs valeurs. */
export const unifiedOrderListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    /** Codes boutique et/ou `pennylane`. Absent = toutes les origines. */
    origin: z.array(z.string().min(1)).optional(),
    status: z.array(unifiedOrderStatusSchema).optional(),
    search: z.string().min(1).optional(),
    /** Réservé aux commandes Shopify : le côté factures est alors sauté. */
    operationId: objectIdSchema.optional(),
    withWarnings: queryBooleanSchema.optional(),
    /**
     * Commandes et factures visées par au moins un événement de ce code entre
     * `alertFrom` et `alertTo` — lien « Alertes des dernières 24 h » du tableau
     * de bord, plus lisible qu'un renvoi vers le Journal.
     */
    alertCode: eventCodeSchema.optional(),
    alertFrom: isoDateTimeSchema.optional(),
    alertTo: isoDateTimeSchema.optional(),
});
export const orderAddressSchema = z.object({
    name: z.string().nullable(),
    company: z.string().nullable(),
    address1: z.string().nullable(),
    address2: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    provinceCode: z.string().nullable(),
    countryCode: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
});
export const orderRelaySchema = z.object({
    code: z.string().nullable(),
    name: z.string().nullable(),
    address1: z.string().nullable(),
    address2: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    countryCode: z.string().nullable(),
});
export const orderAmountsSchema = z.object({
    currency: z.string(),
    shippingExclTax: decimalStringSchema,
    shippingInclTax: decimalStringSchema,
    productsExclTax: decimalStringSchema,
    productsInclTax: decimalStringSchema,
    totalExclTax: decimalStringSchema,
    totalInclTax: decimalStringSchema,
});
export const orderOperationAppliedSchema = z.object({
    operationId: objectIdSchema,
    name: z.string(),
    /** SKU ajoutés — l'unique effet d'une opération (`01` §7.2). */
    skus: z.array(z.string()),
});
export const orderTimelineEntrySchema = z.object({
    at: isoDateTimeSchema,
    code: z.string(),
    message: z.string(),
    userId: objectIdSchema.nullable(),
    userName: z.string().nullable(),
});
export const orderErrorSchema = z.object({
    at: isoDateTimeSchema,
    step: z.string(),
    message: z.string(),
    detail: z.string().nullable(),
});
/** Détail d'une commande (`02` §4). */
export const orderDetailSchema = z.object({
    ...orderListItemSchema.shape,
    orderGid: z.string().nullable(),
    customerId: z.string().nullable(),
    processedAt: isoDateTimeSchema.nullable(),
    shippedAt: isoDateTimeSchema.nullable(),
    tracking: orderTrackingSchema.nullable(),
    shippingAddress: orderAddressSchema.nullable(),
    billingAddress: orderAddressSchema.nullable(),
    relay: orderRelaySchema.nullable(),
    carrierDetail: orderCarrierSchema.nullable(),
    preparationDate: z.string().nullable(),
    customMessage: z.string().nullable(),
    tags: z.array(z.string()),
    amounts: orderAmountsSchema.nullable(),
    lines: z.array(orderLineSchema),
    linesBefore: z.array(orderLineBeforeSchema),
    operationsApplied: z.array(orderOperationAppliedSchema),
    errors: z.array(orderErrorSchema),
    attempts: z.number().int().min(0),
    timeline: z.array(orderTimelineEntrySchema),
    fileIds: z.array(objectIdSchema),
    shopifyAdminUrl: z.string(),
    storeName: z.string().optional(),
    addressChanged: z.boolean().optional(),
    /** `source: 'refresh'` : aperçu issu de « Mettre à jour depuis Shopify » (`01` §4). */
    preview: z.object({ calculatedAt: isoDateTimeSchema, decision: z.string(), source: z.enum(['preparation', 'refresh']), lines: z.array(orderLineSchema), csvContent: z.string().nullable() }).nullable().optional(),
});
/** Aperçu CSV colonne par colonne (`02` §4, onglet CSV). */
export const orderCsvPreviewCellSchema = z.object({
    position: z.number().int().min(1),
    name: z.string(),
    maxLength: z.number().int().positive().nullable(),
    value: z.string(),
    truncated: z.boolean(),
});
export const orderCsvPreviewSchema = z.object({
    /** `null` pour l'aperçu recalculé, qui n'a jamais été envoyé (`?fileId=preview`). */
    fileId: objectIdSchema.nullable(),
    fileName: z.string(),
    header: z.array(orderCsvPreviewCellSchema),
    lines: z.array(z.array(orderCsvPreviewCellSchema)),
    raw: z.string(),
});
/** Corps des actions manuelles de `01` §4. */
export const orderIgnoreBodySchema = z.object({
    reason: z.string().min(1, 'Motif obligatoire'),
});
export const orderBulkActionBodySchema = z.object({
    // Bornée à `MAX_PAGE_SIZE` (`common.ts`) plutôt qu'aux 50 lignes/page par
    // défaut de l'interface (`02` §0) : une sélection « tout sélectionner » sur
    // une page agrandie au maximum autorisé doit rester possible sans être
    // rejetée par l'API.
    orderIds: z.array(objectIdSchema).min(1).max(MAX_PAGE_SIZE),
    reason: z.string().min(1).optional(),
});
export const orderImportBodySchema = z.object({
    store: storeCodeSchema,
    /** Numéro (`#391146`) ou identifiant numérique Shopify. */
    reference: z.string().min(1),
});
/** Résultat de `POST /api/orders/import` : la commande créée ou déjà connue. */
export const orderImportResultSchema = z.object({
    id: objectIdSchema,
    orderId: z.string(),
    /** `false` quand la commande était déjà connue en base (`flows/orders/import.ts`). */
    created: z.boolean(),
});
/** Actions disponibles pour `POST /api/orders/bulk/:action` (`02` §3 : sélection multiple). */
export const ORDER_BULK_ACTIONS = ['send-now', 'regenerate', 'ignore'];
export const orderBulkActionKindSchema = z.enum(ORDER_BULK_ACTIONS);
/** Un échec individuel au sein d'une action groupée, avec le message exact (`02` §0). */
export const orderBulkActionFailureSchema = z.object({
    orderId: objectIdSchema,
    message: z.string(),
});
/** Résultat d'une action groupée : chaque commande réussit ou échoue indépendamment. */
export const orderBulkActionResultSchema = z.object({
    succeeded: z.array(objectIdSchema),
    failed: z.array(orderBulkActionFailureSchema),
});
//# sourceMappingURL=orders.js.map